import ImageWMS from 'ol/source/ImageWMS';
import ImageLayer from 'ol/layer/Image';
import TileLayer from 'ol/layer/Tile';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import WMTS from 'ol/source/WMTS';
import { Extent } from 'ol/extent';
import proj4 from 'proj4';
import { register } from 'ol/proj/proj4';
import { get as getProjection } from 'ol/proj';

// ol.utils.ts
export const projectionExtent: Extent = [500000, 537856, 762144, 800000];
export const flandersExtent: Extent = [260000, 400000, 1000000, 995000];
export const resolutions: number[] = [1024, 512, 256, 128, 64, 32, 16, 8, 4, 2, 1, 0.5, 0.25, 0.125, 0.0625, 0.03125];

const matrixData = new Array(15);
for (let z = 0; z < 16; ++z) {
  matrixData[z] = z;
}
export const matrixIds = matrixData;

export interface IService {
  url: string;
}

export interface ILayer {
  name: string;
  service: IService;
  crs?: string;
  style?: string;
  title?: string;
}

export interface SelectionLayerItem {
  layer: ILayer;
  zIndex?: number;
}

export const LAMBERT_2008 = 'EPSG:3812';
export const LAMBERT_1972 = 'EPSG:31370';
const DEFAULT_WMS_CRS = LAMBERT_2008;

export function createWMSLayer(item: SelectionLayerItem): ImageLayer<ImageWMS> {
  const source = _createWmsSource(item.layer.service.url, item.layer.name, item.layer.crs, item.layer.style);
  const zIndex = item.zIndex;
  const maxZoom = 22; // default max zoom for WMS layers
  const minZoom = 0; // default min zoom for WMS layers
  const opacity = 0.8; // set the opacity a bit lower for the preview layer, so that the underlying map is visible

  const layer = new ImageLayer({
    source: source,
    maxZoom: maxZoom,
    minZoom: minZoom,
    opacity: opacity,
    visible: true,
    zIndex
  });

  return layer;
}

function _createWmsSource(url: string, layerNames: string, crs?: string, style?: string): ImageWMS {
  const source = new ImageWMS({
    url: url,
    projection: crs || DEFAULT_WMS_CRS, // default lambert08
    params: {
      layers: layerNames,
      STYLES: style || '',
      TRANSPARENT: true
    },
    ratio: 1
  });

  return source;
}

export function createGrbGisColorTileLayer() {
  const wmtsSource = new WMTS({
    url: 'https://geo.api.vlaanderen.be/GRB/wmts',
    layer: 'grb_bsk',
    style: '',
    matrixSet: 'BPL2008VL',
    cacheSize: 200,
    format: 'image/png',
    tileGrid: new WMTSTileGrid({
      origin: [500000, 800000],
      extent: projectionExtent,
      resolutions,
      matrixIds
    })
  });

  const layer = new TileLayer({
    source: wmtsSource
  });

  return layer;
}

export function registerOlProjections() {
  if (!proj4.defs('EPSG:31370')) {
    // Source:
    // https://spatialreference.org/ref/epsg/31370/proj4.txt
    proj4.defs(
      'EPSG:31370',
      '+proj=lcc +lat_0=90 +lon_0=4.36748666666667 +lat_1=51.1666672333333 +lat_2=49.8333339 +x_0=150000.013 +y_0=5400088.438 +ellps=intl +units=m +no_defs +type=crs'
    );
  }

  if (!proj4.defs('EPSG:3812')) {
    // Source:
    // https://spatialreference.org/ref/epsg/3812/proj4.txt
    proj4.defs(
      'EPSG:3812',
      '+proj=lcc +lat_0=50.797815 +lon_0=4.35921583333333 +lat_1=49.8333339 +lat_2=51.1666672333333 +x_0=649328 +y_0=665262 +ellps=GRS80 +units=m +no_defs +type=crs'
    );
  }

  register(proj4);

  if (!getProjection('EPSG:31370')) {
    throw new Error('Failed to register EPSG:31370 projection');
  }

  if (!getProjection('EPSG:3812')) {
    throw new Error('Failed to register EPSG:3812 projection');
  }
}
